import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import AdminProjects from "@/pages/AdminProjects";
import ProductForm from "@/components/admin/ProductForm";
import AdminListings from "@/components/admin/AdminListings";
import AdminReferrals from "@/components/admin/AdminReferrals";
import AiCostPanel from "@/components/admin/AiCostPanel";
import TranslateCatalogButton from "@/components/admin/TranslateCatalogButton";
import AdminExperts from "@/components/admin/AdminExperts";
import AdminConsultations from "@/components/admin/AdminConsultations";
import AdminAlerts from "@/components/admin/AdminAlerts";
import AdminCompanies from "@/components/admin/AdminCompanies";
import AdminNews from "@/components/admin/AdminNews";
import { Plus, Pencil, Trash2, Mail, Search as SearchIcon, Check, X, Megaphone, Power, Percent, HeartHandshake } from "lucide-react";
import api, { formatRON, formatApiError, mediaUrl } from "@/lib/api";
import AnalyticsDashboard from "@/components/admin/AnalyticsDashboard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const empty = {
  name: "", category: "02.03.00-noi", description: "", price: 0, stock: 0, image: "", brand: "", condition: "nou",
  specs: { motor_power_kw: "", spindle_diameter_mm: "", weight_kg: "", max_rpm: "", year: "" },
};

const STORY_EMPTY = { title: "", person: "", craft: "", location: "", quote: "", summary: "", image: "", featured: false, published: true, order: 0 };
const IMPACT_KEYS = ["apprentices_placed", "tools_donated", "mentors_engaged", "workshops_supported"];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [cats, setCats] = useState([]);
  const [searchStats, setSearchStats] = useState([]);
  const [listings, setListings] = useState([]);
  const [experts, setExperts] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [commission, setCommission] = useState("");
  const [feeInputs, setFeeInputs] = useState({});
  const [emails, setEmails] = useState([]);
  const [openEmail, setOpenEmail] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [partners, setPartners] = useState([]);
  const [sponsorFee, setSponsorFee] = useState("");
  const [partnerForm, setPartnerForm] = useState({ name: "", tagline: "", description: "", website: "", logo: "", featured: false, order: 0 });
  const [editingPartner, setEditingPartner] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupForm, setGroupForm] = useState({ name: "", platform: "whatsapp", link: "", description: "", region: "", priority_score: 0, active: true });
  const [editingGroup, setEditingGroup] = useState(null);
  const [syndQueue, setSyndQueue] = useState([]);
  const [arvaImpact, setArvaImpact] = useState({ apprentices_placed: "", tools_donated: "", mentors_engaged: "", workshops_supported: "" });
  const [stories, setStories] = useState([]);
  const [storyForm, setStoryForm] = useState(STORY_EMPTY);
  const [editingStory, setEditingStory] = useState(null);
  const [feedbackList, setFeedbackList] = useState([]);
  const [pendingClaims, setPendingClaims] = useState(0);
  const [prodQuery, setProdQuery] = useState("");
  const [prodPage, setProdPage] = useState(1);
  const PROD_PAGE_SIZE = 30;

  const catName = (slug) => (cats.find((c) => c.slug === slug) || {}).name || slug;

  const filteredProducts = products.filter((p) => {
    const q = prodQuery.trim().toLowerCase();
    if (!q) return true;
    return `${p.name} ${p.brand} ${catName(p.category)}`.toLowerCase().includes(q);
  });
  const prodTotalPages = Math.max(1, Math.ceil(filteredProducts.length / PROD_PAGE_SIZE));
  const pageProducts = filteredProducts.slice((prodPage - 1) * PROD_PAGE_SIZE, prodPage * PROD_PAGE_SIZE);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
    api.get("/admin/search-stats").then((r) => setSearchStats(r.data)).catch(() => {});
    api.get("/admin/listings").then((r) => setListings(r.data)).catch(() => {});
    api.get("/admin/experts").then((r) => setExperts(r.data)).catch(() => {});
    api.get("/admin/consultations").then((r) => setConsultations(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => { setCommission(String(r.data.consultation_commission_pct)); setSponsorFee(String(r.data.sponsor_fee_annual)); }).catch(() => {});
    api.get("/admin/email-log").then((r) => setEmails(r.data)).catch(() => {});
    api.get("/admin/sponsors").then((r) => setSponsors(r.data)).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
    api.get("/admin/community-groups").then((r) => setGroups(r.data)).catch(() => {});
    api.get("/admin/syndication-queue").then((r) => setSyndQueue(r.data)).catch(() => {});
    api.get("/arva-stats").then((r) => setArvaImpact({
      apprentices_placed: String(r.data.lifetime.apprentices_placed),
      tools_donated: String(r.data.lifetime.tools_donated),
      mentors_engaged: String(r.data.lifetime.mentors_engaged),
      workshops_supported: String(r.data.lifetime.workshops_supported),
    })).catch(() => {});
    api.get("/admin/success-stories").then((r) => setStories(r.data)).catch(() => {});
    api.get("/admin/feedback").then((r) => setFeedbackList(r.data)).catch(() => {});
    api.get("/admin/company-claims").then((r) => setPendingClaims(r.data.length)).catch(() => {});
  };
  useEffect(() => {
    api.get("/categories/flat").then((r) => setCats(r.data)).catch(() => {});
    loadAll();
  }, []);

  const del = async (id) => {
    if (!window.confirm("Ștergi acest produs?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Produs șters");
    loadAll();
  };

  const approveListing = async (id) => {
    await api.post(`/admin/listings/${id}/approve`);
    toast.success("Anunț aprobat");
    loadAll();
  };
  const rejectListing = async (id) => {
    const reason = window.prompt("Motivul respingerii (opțional):", "");
    if (reason === null) return;
    await api.post(`/admin/listings/${id}/reject`, { reason });
    toast.success("Anunț respins");
    loadAll();
  };
  const pendingCount = listings.filter((l) => l.status === "pending").length;
  const deleteListing = async (id) => {
    if (!window.confirm("Sigur doriți să eliminați acest anunț? Acțiunea nu poate fi anulată.")) return;
    await api.delete(`/admin/listings/${id}`);
    toast.success("Anunț șters");
    loadAll();
  };

  const verifyExpert = async (id) => { await api.post(`/admin/experts/${id}/verify`); toast.success("Expert verificat"); loadAll(); };
  const rejectExpert = async (id) => {
    const reason = window.prompt("Motivul respingerii (opțional):", "");
    if (reason === null) return;
    await api.post(`/admin/experts/${id}/reject`, { reason });
    toast.success("Expert respins"); loadAll();
  };
  const endorseExpert = async (id) => { await api.post(`/admin/experts/${id}/endorse`); toast.success("Status OXO Endorsed actualizat"); loadAll(); };
  const activateExpert = async (id) => { await api.post(`/admin/experts/${id}/activate`); toast.success("Status activ actualizat"); loadAll(); };
  const pendingExperts = experts.filter((e) => e.status === "pending").length;
  const newConsultations = consultations.filter((c) => c.status === "new").length;

  const setConsultStatus = async (id, status) => { await api.post(`/admin/consultations/${id}/status`, { status }); loadAll(); };
  const saveFee = async (c) => {
    const val = Number(feeInputs[c.id] ?? c.agreed_fee);
    if (isNaN(val) || val <= 0) return toast.error("Tarif invalid");
    await api.put(`/admin/consultations/${c.id}/fee`, { agreed_fee: val });
    toast.success("Tarif salvat — link de plată disponibil");
    loadAll();
  };
  const copyPayLink = (c) => {
    const url = `${window.location.origin}/plata-consultanta/${c.id}`;
    navigator.clipboard?.writeText(url);
    toast.success("Link de plată copiat — trimite-l cumpărătorului");
  };
  const togglePayout = async (id) => { await api.post(`/admin/consultations/${id}/payout`); loadAll(); };

  const runReminders = async () => {
    const { data } = await api.post("/admin/run-expiry-reminders?force=true");
    toast.success(`Remindere generate: ${data.reminders_sent}`);
    loadAll();
  };
  const clearEmails = async () => { await api.post("/admin/email-log/clear"); toast.success("Outbox golit"); loadAll(); };
  const saveCommission = async () => {
    const pct = Number(commission);
    if (isNaN(pct) || pct < 0 || pct > 100) return toast.error("Procent invalid (0-100)");
    await api.put("/admin/settings", { consultation_commission_pct: pct });
    toast.success("Comision actualizat");
  };

  // Sponsors
  const verifySponsor = async (id) => { await api.post(`/admin/sponsors/${id}/verify`); toast.success("Sponsor verificat"); loadAll(); };
  const rejectSponsor = async (id) => {
    const reason = window.prompt("Motivul respingerii (opțional):", "");
    if (reason === null) return;
    await api.post(`/admin/sponsors/${id}/reject`, { reason });
    toast.success("Sponsor respins"); loadAll();
  };
  const activateSponsor = async (id) => { await api.post(`/admin/sponsors/${id}/activate`); toast.success("Status sponsor actualizat"); loadAll(); };
  const deleteSponsor = async (id) => { if (!window.confirm("Ștergi definitiv acest sponsor?")) return; await api.delete(`/admin/sponsors/${id}`); toast.success("Sponsor șters"); loadAll(); };
  const pendingSponsors = sponsors.filter((s) => s.status === "pending").length;
  const saveSponsorFee = async () => {
    const fee = Number(sponsorFee);
    if (isNaN(fee) || fee < 0) return toast.error("Tarif invalid");
    await api.put("/admin/settings", { sponsor_fee_annual: fee });
    toast.success("Tarif sponsorizare actualizat");
  };

  // Homepage partners
  const resetPartnerForm = () => { setPartnerForm({ name: "", tagline: "", description: "", website: "", logo: "", featured: false, order: 0 }); setEditingPartner(null); };
  const editPartner = (p) => { setEditingPartner(p.id); setPartnerForm({ name: p.name, tagline: p.tagline || "", description: p.description || "", website: p.website || "", logo: p.logo && !p.logo.startsWith("http") ? p.logo : "", featured: !!p.featured, order: p.order || 0 }); };
  const uploadPartnerLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/admin/partners/upload-logo", fd); setPartnerForm((f) => ({ ...f, logo: r.data.path })); }
    catch { toast.error("Eroare la încărcare"); } finally { e.target.value = ""; }
  };
  const savePartner = async () => {
    if (!partnerForm.name.trim()) return toast.error("Adaugă numele partenerului");
    const payload = { ...partnerForm, order: Number(partnerForm.order) || 0 };
    if (editingPartner) await api.put(`/admin/partners/${editingPartner}`, payload);
    else await api.post("/admin/partners", payload);
    toast.success("Partener salvat"); resetPartnerForm(); loadAll();
  };
  const deletePartner = async (id) => { if (!window.confirm("Ștergi acest partener?")) return; await api.delete(`/admin/partners/${id}`); toast.success("Partener șters"); loadAll(); };

  // Community groups
  const resetGroupForm = () => { setGroupForm({ name: "", platform: "whatsapp", link: "", description: "", region: "", priority_score: 0, active: true }); setEditingGroup(null); };
  const editGroup = (g) => { setEditingGroup(g.id); setGroupForm({ name: g.name, platform: g.platform, link: g.link, description: g.description || "", region: g.region || "", priority_score: g.priority_score || 0, active: g.active }); };
  const saveGroup = async () => {
    if (!groupForm.name.trim() || !groupForm.link.trim()) return toast.error("Nume și link obligatorii");
    const payload = { ...groupForm, priority_score: Number(groupForm.priority_score) || 0 };
    if (editingGroup) await api.put(`/admin/community-groups/${editingGroup}`, payload);
    else await api.post("/admin/community-groups", payload);
    toast.success("Grup salvat"); resetGroupForm(); loadAll();
  };
  const deleteGroup = async (id) => { if (!window.confirm("Ștergi acest grup?")) return; await api.delete(`/admin/community-groups/${id}`); toast.success("Grup șters"); loadAll(); };

  // Syndication queue (manual WhatsApp/social push)
  const syndMessage = (l) => {
    const url = `${window.location.origin}${l.listing_path}`;
    const price = l.price_on_request || !l.price ? "Preț la cerere" : `${new Intl.NumberFormat("ro-RO").format(l.price)} RON`;
    const loc = [l.location, l.county].filter(Boolean).join(", ");
    return `🔥 OXO | ${l.title}\n📍 ${loc}\n💰 ${price}${l.ai_verified_price ? "\n✅ AI Verified Price" : ""}\n👉 ${url}`;
  };
  const copySynd = (l) => { navigator.clipboard.writeText(syndMessage(l)); toast.success("Mesaj copiat în clipboard"); };
  const openWhatsApp = (l) => { window.open(`https://wa.me/?text=${encodeURIComponent(syndMessage(l))}`, "_blank"); };
  const markSyndicated = async (id) => { await api.post(`/admin/listings/${id}/mark-syndicated`); toast.success("Marcat ca distribuit"); loadAll(); };

  // Árva Szakmák — lifetime impact + success stories
  const saveArvaImpact = async () => {
    const payload = {};
    for (const k of IMPACT_KEYS) { const n = Number(arvaImpact[k]); if (!isNaN(n)) payload[k] = Math.max(0, Math.round(n)); }
    await api.put("/admin/arva-impact", payload);
    toast.success("Impact actualizat"); loadAll();
  };
  const resetStoryForm = () => { setStoryForm(STORY_EMPTY); setEditingStory(null); };
  const editStory = (s) => { setEditingStory(s.id); setStoryForm({ title: s.title, person: s.person || "", craft: s.craft || "", location: s.location || "", quote: s.quote || "", summary: s.summary || "", image: s.image && s.image.startsWith("http") ? s.image : (s.image || "").replace(/^\/api\/files\//, ""), featured: !!s.featured, published: s.published !== false, order: s.order || 0 }); };
  const uploadStoryImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/admin/projects/upload-image", fd); setStoryForm((f) => ({ ...f, image: r.data.path })); }
    catch { toast.error("Eroare la încărcare"); } finally { e.target.value = ""; }
  };
  const saveStory = async () => {
    if (!storyForm.title.trim()) return toast.error("Adaugă un titlu");
    const payload = { ...storyForm, order: Number(storyForm.order) || 0 };
    if (editingStory) await api.put(`/admin/success-stories/${editingStory}`, payload);
    else await api.post("/admin/success-stories", payload);
    toast.success("Poveste salvată"); resetStoryForm(); loadAll();
  };
  const deleteStory = async (id) => { if (!window.confirm("Ștergi această poveste?")) return; await api.delete(`/admin/success-stories/${id}`); toast.success("Poveste ștearsă"); loadAll(); };
  const storyImgPreview = (img) => (img ? (img.startsWith("http") ? img : mediaUrl("/api/files/" + img)) : "");

  // Beta feedback
  const toggleFeedback = async (id) => { await api.patch(`/admin/feedback/${id}/resolve`); loadAll(); };
  const deleteFeedback = async (id) => { if (!window.confirm("Ștergi acest feedback?")) return; await api.delete(`/admin/feedback/${id}`); toast.success("Feedback șters"); loadAll(); };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <h1 className="text-4xl font-black tracking-tight mb-2">Panou de administrare</h1>
      <p className="text-slate-500 mb-10">Gestionează produse, comenzi și mesaje.</p>

      <AnalyticsDashboard stats={stats} />

      <Tabs defaultValue="products">
        <TabsList className="rounded-none bg-slate-100 mb-6">
          <TabsTrigger value="products" className="rounded-none" data-testid="tab-products">Produse</TabsTrigger>
          <TabsTrigger value="orders" className="rounded-none" data-testid="tab-orders">Comenzi</TabsTrigger>
          <TabsTrigger value="messages" className="rounded-none" data-testid="tab-messages">Mesaje</TabsTrigger>
          <TabsTrigger value="searches" className="rounded-none" data-testid="tab-searches">Căutări</TabsTrigger>
          <TabsTrigger value="alerts" className="rounded-none" data-testid="tab-alerts">Cereri &amp; Alerte</TabsTrigger>
          <TabsTrigger value="companies" className="rounded-none relative" data-testid="tab-companies">Companii{pendingClaims > 0 && <span className="absolute -top-1.5 -right-1.5 bg-orange-600 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">{pendingClaims}</span>}</TabsTrigger>
          <TabsTrigger value="news" className="rounded-none" data-testid="tab-news">Știri</TabsTrigger>
          <TabsTrigger value="listings" className="rounded-none relative" data-testid="tab-listings">
            Anunțuri{pendingCount > 0 && <span className="ml-1.5 bg-orange-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{pendingCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="experts" className="rounded-none relative" data-testid="tab-experts">
            Experți{pendingExperts > 0 && <span className="ml-1.5 bg-orange-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{pendingExperts}</span>}
          </TabsTrigger>
          <TabsTrigger value="consultations" className="rounded-none relative" data-testid="tab-consultations">
            Consultanță{newConsultations > 0 && <span className="ml-1.5 bg-orange-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{newConsultations}</span>}
          </TabsTrigger>
          <TabsTrigger value="emails" className="rounded-none" data-testid="tab-emails">Email-uri</TabsTrigger>
          <TabsTrigger value="sponsors" className="rounded-none relative" data-testid="tab-sponsors">
            Sponsori{pendingSponsors > 0 && <span className="absolute -top-1.5 -right-1.5 bg-orange-600 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">{pendingSponsors}</span>}
          </TabsTrigger>
          <TabsTrigger value="partners" className="rounded-none" data-testid="tab-partners">Parteneri</TabsTrigger>
          <TabsTrigger value="affiliate" className="rounded-none" data-testid="tab-affiliate">Afiliați</TabsTrigger>
          <TabsTrigger value="ai-costs" className="rounded-none" data-testid="tab-ai-costs">Costuri AI</TabsTrigger>
          <TabsTrigger value="community" className="rounded-none" data-testid="tab-community">Comunitate</TabsTrigger>
          <TabsTrigger value="syndication" className="rounded-none relative" data-testid="tab-syndication">
            Syndicare{syndQueue.length > 0 && <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-slate-900 text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">{syndQueue.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="projects" className="rounded-none" data-testid="tab-projects">Proiecte</TabsTrigger>
          <TabsTrigger value="arva" className="rounded-none" data-testid="tab-arva">Árva / Impact</TabsTrigger>
          <TabsTrigger value="feedback" className="rounded-none" data-testid="tab-feedback">Feedback{feedbackList.filter((f) => !f.resolved).length > 0 ? ` (${feedbackList.filter((f) => !f.resolved).length})` : ""}</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="flex justify-between items-center mb-5">
            <h2 className="font-bold text-lg">Produse ({products.length})</h2>
            <div className="flex items-center gap-2">
              <TranslateCatalogButton />
              <ProductForm initial={empty} onSaved={loadAll} cats={cats} />
            </div>
          </div>
          <div className="mb-4 relative">
            <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={prodQuery}
              onChange={(e) => { setProdQuery(e.target.value); setProdPage(1); }}
              placeholder="Caută după nume, marcă sau categorie…"
              className="rounded-none pl-9"
              data-testid="admin-product-search"
            />
          </div>
          <div className="border border-slate-200 divide-y divide-slate-100">
            {pageProducts.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`admin-product-${p.id}`}>
                <img src={p.image || "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?crop=entropy&cs=srgb&fm=jpg&q=80&w=200"} alt={p.name} loading="lazy" decoding="async" className="w-14 h-14 object-cover bg-slate-100 border border-slate-200" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.brand} · {catName(p.category)} · Stoc: {p.stock}</div>
                </div>
                <div className="font-black">{formatRON(p.price)}</div>
                <ProductForm initial={{ ...p, specs: { motor_power_kw: "", spindle_diameter_mm: "", weight_kg: "", max_rpm: "", year: "", ...p.specs } }} onSaved={loadAll} cats={cats} />
                <button onClick={() => del(p.id)} className="p-2 hover:bg-red-50 text-red-600" data-testid={`delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {pageProducts.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">Niciun produs găsit.</div>}
          </div>
          {prodTotalPages > 1 && (
            <div className="flex items-center justify-between mt-4" data-testid="admin-product-pagination">
              <button
                onClick={() => setProdPage((p) => Math.max(1, p - 1))}
                disabled={prodPage === 1}
                className="border border-slate-300 hover:border-slate-900 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 text-xs font-bold uppercase transition-colors"
                data-testid="admin-product-prev"
              >Înapoi</button>
              <span className="text-sm text-slate-500">Pagina {prodPage} / {prodTotalPages} · {filteredProducts.length} produse</span>
              <button
                onClick={() => setProdPage((p) => Math.min(prodTotalPages, p + 1))}
                disabled={prodPage === prodTotalPages}
                className="border border-slate-300 hover:border-slate-900 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 text-xs font-bold uppercase transition-colors"
                data-testid="admin-product-next"
              >Înainte</button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders">
          <h2 className="font-bold text-lg mb-5">Comenzi ({orders.length})</h2>
          <div className="border border-slate-200 divide-y divide-slate-100">
            {orders.length === 0 && <div className="p-8 text-center text-slate-400">Nicio comandă.</div>}
            {orders.map((o) => (
              <div key={o.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-order-${o.id}`}>
                <div>
                  <div className="font-mono text-xs text-slate-400">#{o.id.slice(-8)}</div>
                  <div className="text-sm text-slate-600">{o.user_email}</div>
                  <div className="text-xs text-slate-400">{new Date(o.created_at).toLocaleString("ro-RO")}</div>
                </div>
                <div className="text-sm text-slate-500">{o.items.length} produse</div>
                <div className="font-black">{formatRON(o.amount)}</div>
                <Badge className={`rounded-none ${o.payment_status === "paid" ? "bg-green-600" : "bg-slate-400"}`}>
                  {o.payment_status === "paid" ? "Plătit" : "În așteptare"}
                </Badge>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="messages">
          <h2 className="font-bold text-lg mb-5">Mesaje ({messages.length})</h2>
          <div className="space-y-3">
            {messages.length === 0 && <div className="border border-slate-200 p-8 text-center text-slate-400">Niciun mesaj.</div>}
            {messages.map((m) => (
              <div key={m.id} className="border border-slate-200 p-5" data-testid={`message-${m.id}`}>
                <div className="flex items-center gap-2 font-semibold"><Mail className="w-4 h-4 text-orange-600" /> {m.name} <span className="text-sm text-slate-400 font-normal">· {m.email} {m.phone && `· ${m.phone}`}</span></div>
                <p className="text-slate-600 mt-2 text-sm">{m.message}</p>
                <div className="text-xs text-slate-400 mt-2">{new Date(m.created_at).toLocaleString("ro-RO")}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="searches">
          <h2 className="font-bold text-lg mb-5">Top căutări ({searchStats.length})</h2>
          {searchStats.length === 0 ? (
            <div className="border border-slate-200 p-8 text-center text-slate-400">Nicio căutare înregistrată încă.</div>
          ) : (
            <div className="border border-slate-200">
              <div className="grid grid-cols-12 px-4 py-3 bg-slate-900 text-white text-[11px] uppercase tracking-wide font-bold">
                <span className="col-span-6">Termen căutat</span>
                <span className="col-span-2 text-right">Căutări</span>
                <span className="col-span-4 text-right">Ultima dată</span>
              </div>
              <div className="divide-y divide-slate-100">
                {searchStats.map((s, i) => (
                  <div key={s.query} className="grid grid-cols-12 items-center px-4 py-3 text-sm hover:bg-slate-50" data-testid={`search-stat-${i}`}>
                    <span className="col-span-6 font-semibold text-slate-900 truncate flex items-center gap-2">
                      <SearchIcon className="w-4 h-4 text-orange-600 shrink-0" /> {s.query}
                    </span>
                    <span className="col-span-2 text-right font-mono font-bold">{s.count}</span>
                    <span className="col-span-4 text-right text-xs text-slate-400">{s.last_at ? new Date(s.last_at).toLocaleString("ro-RO") : "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="listings">
          <AdminListings
            listings={listings}
            pendingCount={pendingCount}
            catName={catName}
            onApprove={approveListing}
            onReject={rejectListing}
            onDelete={deleteListing}
            onSaved={loadAll}
          />
        </TabsContent>

        <TabsContent value="experts">
          <AdminExperts
            experts={experts}
            pendingExperts={pendingExperts}
            onVerify={verifyExpert}
            onReject={rejectExpert}
            onEndorse={endorseExpert}
            onActivate={activateExpert}
          />
        </TabsContent>

        <TabsContent value="consultations">
          <AdminConsultations
            consultations={consultations}
            newConsultations={newConsultations}
            stats={stats}
            commission={commission}
            setCommission={setCommission}
            saveCommission={saveCommission}
            feeInputs={feeInputs}
            setFeeInputs={setFeeInputs}
            saveFee={saveFee}
            copyPayLink={copyPayLink}
            togglePayout={togglePayout}
            setConsultStatus={setConsultStatus}
          />
        </TabsContent>

        <TabsContent value="emails">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div>
              <h2 className="font-bold text-lg">Email Outbox <span className="text-sm font-normal text-slate-500">(mock — Resend neconfigurat)</span></h2>
              <p className="text-sm text-slate-500 mt-0.5">Emailurile sunt înregistrate aici pentru testare. Adaugă RESEND_API_KEY pentru a le trimite real.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={runReminders} className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm font-bold uppercase transition-colors" data-testid="run-reminders">Trimite remindere expirare</button>
              <button onClick={clearEmails} className="border border-slate-300 hover:border-slate-900 px-4 py-2 text-sm font-bold uppercase transition-colors" data-testid="clear-emails">Golește</button>
            </div>
          </div>
          <div className="space-y-2">
            {emails.length === 0 && <div className="border border-slate-200 p-8 text-center text-slate-400">Niciun email în outbox.</div>}
            {emails.map((m) => (
              <div key={m.id} className="border border-slate-200" data-testid={`email-${m.id}`}>
                <button onClick={() => setOpenEmail(openEmail === m.id ? null : m.id)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{m.subject}</div>
                    <div className="text-xs text-slate-500">→ {m.to} · {new Date(m.created_at).toLocaleString("ro-RO")}</div>
                  </div>
                  <Badge className={`rounded-none shrink-0 ${m.status === "sent" ? "bg-green-600" : m.status === "failed" ? "bg-red-600" : "bg-slate-500"}`}>{m.status}</Badge>
                </button>
                {openEmail === m.id && (
                  <div className="border-t border-slate-200 p-4 bg-slate-50">
                    {m.action_link && (
                      <a href={m.action_link} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors mb-3"
                        data-testid={`email-action-link-${m.id}`}>
                        Deschide linkul →
                      </a>
                    )}
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.html || "") }} data-testid={`email-body-${m.id}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="projects">
          <AdminProjects />
        </TabsContent>

        <TabsContent value="sponsors">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <div>
              <h2 className="font-bold text-lg">Producători parteneri ({sponsors.length}) · {pendingSponsors} în așteptare</h2>
              <p className="text-sm text-slate-500 mt-0.5">Sponsorii verificați + activi primesc badge „Partener oficial" pe produsele mărcii lor și prioritate în catalog.</p>
            </div>
            <div className="flex items-end gap-2" data-testid="sponsor-fee-setting">
              <div>
                <label className="text-xs uppercase tracking-wide font-bold text-slate-500 block mb-1">Tarif sponsorizare (RON/an)</label>
                <Input type="number" value={sponsorFee} onChange={(e) => setSponsorFee(e.target.value)} className="rounded-none w-36" data-testid="sponsor-fee-input" />
              </div>
              <button onClick={saveSponsorFee} className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-bold uppercase transition-colors h-10" data-testid="save-sponsor-fee"><Percent className="w-4 h-4" /> Salvează</button>
            </div>
          </div>
          <div className="space-y-3">
            {sponsors.length === 0 && <div className="border border-slate-200 p-8 text-center text-slate-400">Niciun sponsor.</div>}
            {sponsors.map((s) => (
              <div key={s.id} className="border border-slate-200 p-4 flex flex-col sm:flex-row gap-4" data-testid={`admin-sponsor-${s.id}`}>
                <div className="w-full sm:w-20 h-20 bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                  {s.logo ? <img src={mediaUrl(s.logo)} alt={s.company} className="w-full h-full object-contain" /> : <Megaphone className="w-7 h-7 text-slate-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="font-semibold flex items-center gap-2">{s.company} {s.brand && <span className="text-orange-600 text-xs font-bold uppercase">[{s.brand}]</span>}</div>
                    <div className="flex items-center gap-2">
                      <Badge className={`rounded-none ${s.status === "verified" ? "bg-green-600" : s.status === "rejected" ? "bg-red-600" : "bg-amber-500"}`}>{s.status === "verified" ? "Verificat" : s.status === "rejected" ? "Respins" : "În așteptare"}</Badge>
                      <Badge className={`rounded-none ${s.active ? "bg-slate-900" : "bg-slate-300 text-slate-700"}`}>{s.active ? "Public" : "Inactiv"}</Badge>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{s.contact_email}{s.contact_phone ? ` · ${s.contact_phone}` : ""}{s.website ? ` · ${s.website}` : ""}</div>
                  {s.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{s.description}</p>}
                  {s.paid_until && <div className="text-xs text-slate-400 mt-1">Activ până la {new Date(s.paid_until).toLocaleDateString("ro-RO")}</div>}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {s.status !== "verified" && <button onClick={() => verifySponsor(s.id)} className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`verify-sponsor-${s.id}`}><Check className="w-3.5 h-3.5" /> Verifică</button>}
                    {s.status !== "rejected" && <button onClick={() => rejectSponsor(s.id)} className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-800 text-white px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`reject-sponsor-${s.id}`}><X className="w-3.5 h-3.5" /> Respinge</button>}
                    <button onClick={() => activateSponsor(s.id)} className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-slate-900 px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`activate-sponsor-${s.id}`}><Power className="w-3.5 h-3.5" /> {s.active ? "Dezactivează" : "Activează (comp)"}</button>
                    <button onClick={() => deleteSponsor(s.id)} className="inline-flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`delete-sponsor-${s.id}`}><Trash2 className="w-3.5 h-3.5" /> Șterge</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="partners">
          <h2 className="font-bold text-lg mb-1">Parteneri tehnologici (homepage)</h2>
          <p className="text-sm text-slate-500 mb-5">Editează secțiunea „Parteneri tehnologici" de pe pagina principală. Bifează „Featured" pentru partenerul principal evidențiat.</p>
          <div className="grid lg:grid-cols-[1fr_360px] gap-6">
            <div className="space-y-3">
              {partners.length === 0 && <div className="border border-slate-200 p-8 text-center text-slate-400">Niciun partener.</div>}
              {partners.map((p) => (
                <div key={p.id} className="border border-slate-200 p-4 flex gap-4" data-testid={`admin-partner-${p.id}`}>
                  <div className="w-16 h-16 bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                    {p.logo ? <img src={mediaUrl(p.logo)} alt={p.name} className="w-full h-full object-contain" /> : <Megaphone className="w-6 h-6 text-slate-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 font-semibold">{p.name}{p.featured && <span className="text-[10px] bg-orange-600 text-white px-1.5 py-0.5 uppercase font-bold">Featured</span>}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.tagline}</div>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => editPartner(p)} className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-slate-900 px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`edit-partner-${p.id}`}><Pencil className="w-3.5 h-3.5" /> Editează</button>
                      <button onClick={() => deletePartner(p.id)} className="inline-flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`delete-partner-${p.id}`}><Trash2 className="w-3.5 h-3.5" /> Șterge</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border border-slate-200 p-5 h-fit" data-testid="partner-form">
              <h3 className="font-bold mb-4">{editingPartner ? "Editează partener" : "Adaugă partener"}</h3>
              <div className="space-y-3">
                <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Nume *</label><Input value={partnerForm.name} onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })} className="rounded-none mt-1" data-testid="partner-name" /></div>
                <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Tagline</label><Input value={partnerForm.tagline} onChange={(e) => setPartnerForm({ ...partnerForm, tagline: e.target.value })} className="rounded-none mt-1" data-testid="partner-tagline" /></div>
                <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Descriere</label><Textarea value={partnerForm.description} onChange={(e) => setPartnerForm({ ...partnerForm, description: e.target.value })} className="rounded-none mt-1" data-testid="partner-desc" /></div>
                <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Website</label><Input value={partnerForm.website} onChange={(e) => setPartnerForm({ ...partnerForm, website: e.target.value })} placeholder="https://..." className="rounded-none mt-1" data-testid="partner-website" /></div>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 border border-slate-300 hover:border-orange-600 px-3 py-2 text-sm cursor-pointer transition-colors"><Plus className="w-4 h-4" /> Logo<input type="file" accept="image/*" className="hidden" onChange={uploadPartnerLogo} data-testid="partner-logo-input" /></label>
                  {partnerForm.logo && <img src={mediaUrl(partnerForm.logo)} alt="" className="h-10 object-contain" />}
                </div>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Ordine</label><Input type="number" value={partnerForm.order} onChange={(e) => setPartnerForm({ ...partnerForm, order: e.target.value })} className="rounded-none mt-1" data-testid="partner-order" /></div>
                  <label className="flex items-center gap-2 pb-2.5 cursor-pointer"><input type="checkbox" checked={partnerForm.featured} onChange={(e) => setPartnerForm({ ...partnerForm, featured: e.target.checked })} data-testid="partner-featured" /><span className="text-sm">Featured</span></label>
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={savePartner} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 text-sm font-bold uppercase transition-colors" data-testid="save-partner">{editingPartner ? "Salvează" : "Adaugă"}</button>
                  {editingPartner && <button onClick={resetPartnerForm} className="border border-slate-300 hover:border-slate-900 px-4 py-2.5 text-sm font-bold uppercase transition-colors">Anulează</button>}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="affiliate">
          <AdminReferrals />
        </TabsContent>
        <TabsContent value="ai-costs">
          <AiCostPanel />
        </TabsContent>
        <TabsContent value="community">
          <h2 className="font-bold text-lg mb-1">Comunitate / Grupuri de Profil</h2>
          <p className="text-sm text-slate-500 mb-5">Gestionează grupurile WhatsApp/Facebook. Un <b>priority_score</b> mai mare = plasare „sticky" în topul Hub-ului (vândut administratorilor de grupuri).</p>
          <div className="grid lg:grid-cols-[1fr_360px] gap-6">
            <div className="space-y-3">
              {groups.length === 0 && <div className="border border-slate-200 p-8 text-center text-slate-400">Niciun grup.</div>}
              {groups.map((g) => (
                <div key={g.id} className="border border-slate-200 p-4 flex justify-between gap-4" data-testid={`admin-group-${g.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-semibold">{g.name}{g.priority_score > 0 && <span className="text-[10px] bg-orange-600 text-white px-1.5 py-0.5 uppercase font-bold">Prio {g.priority_score}</span>}{!g.active && <span className="text-[10px] bg-slate-300 text-slate-700 px-1.5 py-0.5 uppercase font-bold">Inactiv</span>}</div>
                    <div className="text-xs text-slate-500 mt-0.5 capitalize">{g.platform}{g.region ? ` · ${g.region}` : ""}</div>
                    <a href={g.link} target="_blank" rel="noreferrer" className="text-xs text-orange-600 hover:underline break-all">{g.link}</a>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => editGroup(g)} className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-slate-900 px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`edit-group-${g.id}`}><Pencil className="w-3.5 h-3.5" /> Editează</button>
                      <button onClick={() => deleteGroup(g.id)} className="inline-flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`delete-group-${g.id}`}><Trash2 className="w-3.5 h-3.5" /> Șterge</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border border-slate-200 p-5 h-fit" data-testid="group-form">
              <h3 className="font-bold mb-4">{editingGroup ? "Editează grup" : "Adaugă grup"}</h3>
              <div className="space-y-3">
                <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Nume *</label><Input value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} className="rounded-none mt-1" data-testid="group-name" /></div>
                <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Link *</label><Input value={groupForm.link} onChange={(e) => setGroupForm({ ...groupForm, link: e.target.value })} placeholder="https://chat.whatsapp.com/..." className="rounded-none mt-1" data-testid="group-link" /></div>
                <div>
                  <label className="text-xs uppercase tracking-wide font-bold text-slate-500">Platformă</label>
                  <select value={groupForm.platform} onChange={(e) => setGroupForm({ ...groupForm, platform: e.target.value })} className="w-full border border-slate-300 rounded-none mt-1 px-3 py-2 text-sm" data-testid="group-platform">
                    <option value="whatsapp">WhatsApp</option><option value="facebook">Facebook</option><option value="telegram">Telegram</option><option value="other">Altele</option>
                  </select>
                </div>
                <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Regiune</label><Input value={groupForm.region} onChange={(e) => setGroupForm({ ...groupForm, region: e.target.value })} placeholder="Ex: Transilvania" className="rounded-none mt-1" data-testid="group-region" /></div>
                <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Descriere</label><Textarea value={groupForm.description} onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })} className="rounded-none mt-1" data-testid="group-desc" /></div>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Priority score</label><Input type="number" value={groupForm.priority_score} onChange={(e) => setGroupForm({ ...groupForm, priority_score: e.target.value })} className="rounded-none mt-1" data-testid="group-priority" /></div>
                  <label className="flex items-center gap-2 pb-2.5 cursor-pointer"><input type="checkbox" checked={groupForm.active} onChange={(e) => setGroupForm({ ...groupForm, active: e.target.checked })} data-testid="group-active" /><span className="text-sm">Activ</span></label>
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={saveGroup} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 text-sm font-bold uppercase transition-colors" data-testid="save-group">{editingGroup ? "Salvează" : "Adaugă"}</button>
                  {editingGroup && <button onClick={resetGroupForm} className="border border-slate-300 hover:border-slate-900 px-4 py-2.5 text-sm font-bold uppercase transition-colors">Anulează</button>}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>


        <TabsContent value="syndication">
          <h2 className="font-bold text-lg mb-1">Coadă de syndicare (push manual)</h2>
          <p className="text-sm text-slate-500 mb-5">Anunțuri promovate Gold VIP, gata de distribuit în grupurile WhatsApp/Facebook. Copiază mesajul pre-formatat sau deschide WhatsApp, apoi marchează ca distribuit.</p>
          <div className="space-y-3">
            {syndQueue.length === 0 && <div className="border border-slate-200 p-8 text-center text-slate-400" data-testid="synd-empty">Niciun anunț în coada de syndicare.</div>}
            {syndQueue.map((l) => (
              <div key={l.id} className="border border-slate-200 p-4" data-testid={`synd-item-${l.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold flex items-center gap-2">{l.title}{l.ai_verified_price && <span className="text-[10px] bg-amber-500 text-slate-900 px-1.5 py-0.5 uppercase font-bold">AI Verified</span>}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{[l.location, l.county].filter(Boolean).join(", ")} · {l.price_on_request || !l.price ? "Preț la cerere" : formatRON(l.price)}</div>
                  </div>
                </div>
                <pre className="text-xs bg-slate-50 border border-slate-100 p-3 mt-3 whitespace-pre-wrap font-sans text-slate-600" data-testid={`synd-message-${l.id}`}>{syndMessage(l)}</pre>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => copySynd(l)} className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-slate-900 px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`synd-copy-${l.id}`}><Plus className="w-3.5 h-3.5" /> Copiază mesaj</button>
                  <button onClick={() => openWhatsApp(l)} className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`synd-whatsapp-${l.id}`}>Deschide WhatsApp</button>
                  <button onClick={() => markSyndicated(l.id)} className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`synd-done-${l.id}`}><Check className="w-3.5 h-3.5" /> Marchează distribuit</button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="arva">
          <h2 className="font-bold text-lg mb-1">Árva Szakmák — Impact & Povești de succes</h2>
          <p className="text-sm text-slate-500 mb-5">Actualizează tally-ul permanent de impact (afișat pe pagina Árva Szakmák) și gestionează poveștile de succes promovate pe homepage și pe pagina programului.</p>

          {/* Lifetime impact editor */}
          <div className="border border-emerald-200 bg-emerald-50/60 p-5 mb-8" data-testid="arva-impact-form">
            <h3 className="font-bold mb-4 flex items-center gap-2"><HeartHandshake className="w-5 h-5 text-emerald-600" /> Impact de durată (tally permanent)</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { k: "apprentices_placed", label: "Ucenici plasați" },
                { k: "tools_donated", label: "Utilaje & scule donate" },
                { k: "mentors_engaged", label: "Mentori implicați" },
                { k: "workshops_supported", label: "Ateliere sprijinite" },
              ].map((f) => (
                <div key={f.k}>
                  <label className="text-xs uppercase tracking-wide font-bold text-slate-500">{f.label}</label>
                  <Input type="number" value={arvaImpact[f.k]} onChange={(e) => setArvaImpact({ ...arvaImpact, [f.k]: e.target.value })} className="rounded-none mt-1" data-testid={`impact-${f.k}`} />
                </div>
              ))}
            </div>
            <button onClick={saveArvaImpact} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 text-sm font-bold uppercase transition-colors" data-testid="save-impact">Salvează impactul</button>
          </div>

          {/* Success stories CRUD */}
          <h3 className="font-bold mb-4">Povești de succes</h3>
          <div className="grid lg:grid-cols-[1fr_360px] gap-6">
            <div className="space-y-3">
              {stories.length === 0 && <div className="border border-slate-200 p-8 text-center text-slate-400">Nicio poveste.</div>}
              {stories.map((s) => (
                <div key={s.id} className="border border-slate-200 p-4 flex gap-4" data-testid={`admin-story-${s.id}`}>
                  <div className="w-20 h-20 bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 overflow-hidden">
                    {s.image ? <img src={mediaUrl(s.image)} alt={s.title} className="w-full h-full object-cover" /> : <HeartHandshake className="w-6 h-6 text-slate-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 font-semibold">{s.title}{s.featured && <span className="text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 uppercase font-bold">Featured</span>}{!s.published && <span className="text-[10px] bg-slate-300 text-slate-700 px-1.5 py-0.5 uppercase font-bold">Draft</span>}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{[s.person, s.craft, s.location].filter(Boolean).join(" · ")}</div>
                    {s.quote && <p className="text-xs text-emerald-700 italic mt-1 line-clamp-1">„{s.quote}"</p>}
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => editStory(s)} className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-slate-900 px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`edit-story-${s.id}`}><Pencil className="w-3.5 h-3.5" /> Editează</button>
                      <button onClick={() => deleteStory(s.id)} className="inline-flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`delete-story-${s.id}`}><Trash2 className="w-3.5 h-3.5" /> Șterge</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border border-slate-200 p-5 h-fit" data-testid="story-form">
              <h3 className="font-bold mb-4">{editingStory ? "Editează poveste" : "Adaugă poveste"}</h3>
              <div className="space-y-3">
                <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Titlu *</label><Input value={storyForm.title} onChange={(e) => setStoryForm({ ...storyForm, title: e.target.value })} className="rounded-none mt-1" data-testid="story-title" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Persoană</label><Input value={storyForm.person} onChange={(e) => setStoryForm({ ...storyForm, person: e.target.value })} className="rounded-none mt-1" data-testid="story-person" /></div>
                  <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Meșteșug</label><Input value={storyForm.craft} onChange={(e) => setStoryForm({ ...storyForm, craft: e.target.value })} className="rounded-none mt-1" data-testid="story-craft" /></div>
                </div>
                <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Localitate</label><Input value={storyForm.location} onChange={(e) => setStoryForm({ ...storyForm, location: e.target.value })} className="rounded-none mt-1" data-testid="story-location" /></div>
                <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Citat</label><Input value={storyForm.quote} onChange={(e) => setStoryForm({ ...storyForm, quote: e.target.value })} className="rounded-none mt-1" data-testid="story-quote" /></div>
                <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Descriere</label><Textarea value={storyForm.summary} onChange={(e) => setStoryForm({ ...storyForm, summary: e.target.value })} className="rounded-none mt-1" data-testid="story-summary" /></div>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 border border-slate-300 hover:border-emerald-600 px-3 py-2 text-sm cursor-pointer transition-colors"><Plus className="w-4 h-4" /> Imagine<input type="file" accept="image/*" className="hidden" onChange={uploadStoryImage} data-testid="story-image-input" /></label>
                  {storyForm.image && <img src={storyImgPreview(storyForm.image)} alt="" className="h-10 object-cover" />}
                </div>
                <div className="grid grid-cols-3 gap-3 items-end">
                  <div><label className="text-xs uppercase tracking-wide font-bold text-slate-500">Ordine</label><Input type="number" value={storyForm.order} onChange={(e) => setStoryForm({ ...storyForm, order: e.target.value })} className="rounded-none mt-1" data-testid="story-order" /></div>
                  <label className="flex items-center gap-2 pb-2.5 cursor-pointer"><input type="checkbox" checked={storyForm.featured} onChange={(e) => setStoryForm({ ...storyForm, featured: e.target.checked })} data-testid="story-featured" /><span className="text-sm">Featured</span></label>
                  <label className="flex items-center gap-2 pb-2.5 cursor-pointer"><input type="checkbox" checked={storyForm.published} onChange={(e) => setStoryForm({ ...storyForm, published: e.target.checked })} data-testid="story-published" /><span className="text-sm">Public</span></label>
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={saveStory} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 text-sm font-bold uppercase transition-colors" data-testid="save-story">{editingStory ? "Salvează" : "Adaugă"}</button>
                  {editingStory && <button onClick={resetStoryForm} className="border border-slate-300 hover:border-slate-900 px-4 py-2.5 text-sm font-bold uppercase transition-colors">Anulează</button>}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="alerts">
          <AdminAlerts />
        </TabsContent>

        <TabsContent value="companies">
          <AdminCompanies />
        </TabsContent>

        <TabsContent value="news">
          <AdminNews />
        </TabsContent>

        <TabsContent value="feedback">
          <h2 className="font-bold text-lg mb-1">Feedback beta</h2>
          <p className="text-sm text-slate-500 mb-5">Sugestiile și bug-urile trimise de testeri prin widget-ul flotant.</p>
          {feedbackList.length === 0 && <div className="border border-slate-200 p-8 text-center text-slate-400">Niciun feedback încă.</div>}
          <div className="space-y-3">
            {feedbackList.map((f) => (
              <div key={f.id} className={`border p-4 ${f.resolved ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-300"}`} data-testid={`feedback-${f.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 ${f.type === "bug" ? "bg-red-100 text-red-700" : f.type === "idea" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}>{f.type}</span>
                  {f.page && <span className="text-xs text-slate-400 font-mono">{f.page}</span>}
                  {f.resolved && <span className="text-[10px] uppercase font-bold bg-slate-300 text-slate-700 px-1.5 py-0.5">Rezolvat</span>}
                </div>
                <p className="text-sm text-slate-800 whitespace-pre-line">{f.message}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-slate-400">{f.email || "—"} · {new Date(f.created_at).toLocaleString("ro-RO")}</span>
                  <div className="flex gap-2">
                    <button onClick={() => toggleFeedback(f.id)} className="border border-slate-300 hover:border-slate-900 px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`feedback-toggle-${f.id}`}>{f.resolved ? "Redeschide" : "Rezolvat"}</button>
                    <button onClick={() => deleteFeedback(f.id)} className="border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`feedback-delete-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

      </Tabs>
    </div>
  );
}